import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ValuationTab, InfoPanels, GovernanceTab, DocumentsTab, CommunicationsTab, OwnershipTab, TransactionsTab, FinancialsTab, TasksTab } from '@/components/llc';
import { EditInfoDialog, DocumentDialog, CommunicationEditDialog, CommunicationViewDialog, EditLLCDialog, AddOwnershipDialog, PropertyLookupDialog, DeleteLLCDialog } from '@/components/dialogs';
import { toast } from 'sonner';
import axios from 'axios';
import { formatCurrency } from '@/utils/formatters';
import {
  Building2, Briefcase, DollarSign, ArrowLeft, ChevronLeft, ChevronRight,
  Loader2, Edit, Trash2, Users, CreditCard, FileText, TrendingUp, TrendingDown,
  Calendar, MessageSquare, FolderOpen, Shield, Star, ListTodo, Printer,
} from 'lucide-react';

import { API } from '../utils/api';
import { openPrintWindow } from '../utils/openPrintWindow';

const LLCDetail = () => {
  const { llcId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { getAuthHeaders, user } = useAuth();
  const [llc, setLLC] = useState(null);
  const [allLLCs, setAllLLCs] = useState([]);
  const [ownerships, setOwnerships] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [investors, setInvestors] = useState([]);
  const [balanceSheet, setBalanceSheet] = useState(null);
  const [llcTasks, setLlcTasks] = useState([]);
  const [loading, setLoading] = useState(true);

  // Dialog states
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editPropertyDialogOpen, setEditPropertyDialogOpen] = useState(false);
  const [editAccountantDialogOpen, setEditAccountantDialogOpen] = useState(false);
  const [editBankDialogOpen, setEditBankDialogOpen] = useState(false);
  const [editLenderDialogOpen, setEditLenderDialogOpen] = useState(false);
  const [editTenantDialogOpen, setEditTenantDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [ownershipDialogOpen, setOwnershipDialogOpen] = useState(false);
  const [documentDialogOpen, setDocumentDialogOpen] = useState(false);
  const [communicationDialogOpen, setCommunicationDialogOpen] = useState(false);
  const [communicationViewOpen, setCommunicationViewOpen] = useState(false);
  const [propertyLookupOpen, setPropertyLookupOpen] = useState(false);

  // Form states
  const [formLoading, setFormLoading] = useState(false);
  const [formData, setFormData] = useState({});
  const [ownershipForm, setOwnershipForm] = useState({ investor_id: '', percentage: '' });
  const [documents, setDocuments] = useState([]);
  const [communications, setCommunications] = useState([]);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [selectedCommunication, setSelectedCommunication] = useState(null);
  const [viewingCommunication, setViewingCommunication] = useState(null);
  const [documentForm, setDocumentForm] = useState({ document_type: 'other', name: '', description: '' });
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [communicationForm, setCommunicationForm] = useState({
    date: new Date().toISOString().split('T')[0], to_party: '', about: '', notes: '', created_by_name: ''
  });

  // Tab management
  const activeTab = searchParams.get('tab') || 'communications';
  const setActiveTab = (tab) => setSearchParams({ tab });

  const fetchData = async (id) => {
    const targetId = id || llcId;
    try {
      const [llcRes, ownershipsRes, transactionsRes, investorsRes, balanceSheetRes, allLLCsRes, documentsRes, communicationsRes, tasksRes] = await Promise.all([
        axios.get(`${API}/llcs/${targetId}`, { headers: getAuthHeaders() }),
        axios.get(`${API}/ownerships?llc_id=${targetId}`, { headers: getAuthHeaders() }),
        axios.get(`${API}/transactions?llc_id=${targetId}`, { headers: getAuthHeaders() }),
        axios.get(`${API}/investors`, { headers: getAuthHeaders() }),
        axios.get(`${API}/reports/balance-sheet/${targetId}`, { headers: getAuthHeaders() }),
        axios.get(`${API}/llcs`, { headers: getAuthHeaders() }),
        axios.get(`${API}/documents?llc_id=${targetId}`, { headers: getAuthHeaders() }),
        axios.get(`${API}/communications?llc_id=${targetId}`, { headers: getAuthHeaders() }),
        axios.get(`${API}/tasks/llc/${targetId}`, { headers: getAuthHeaders() }).catch(() => ({ data: [] }))
      ]);
      setLLC(llcRes.data);
      setOwnerships(ownershipsRes.data);
      setTransactions(transactionsRes.data);
      setInvestors(investorsRes.data);
      const sortedLLCs = [...allLLCsRes.data].sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));
      setAllLLCs(sortedLLCs);
      setBalanceSheet(balanceSheetRes.data);
      setFormData(llcRes.data);
      setDocuments(documentsRes.data);
      setCommunications(communicationsRes.data);
      setLlcTasks(tasksRes.data);
    } catch (error) {
      console.error('Fetch error:', error);
      toast.error('Failed to load LLC details');
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    setLLC(null);
    fetchData(llcId);
  }, [llcId]);

  // Handlers
  const handleUpdate = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    try {
      await axios.put(`${API}/llcs/${llcId}`, {
        name: formData.name, llc_type: formData.llc_type,
        property_address: formData.property_address, property_description: formData.property_description,
        property_type: formData.property_type || null, property_year_built: formData.property_year_built || null,
        property_year_acquired: formData.property_year_acquired || null, property_size_sqft: formData.property_size_sqft || null,
        property_acreage: formData.property_acreage || null,
        accountant_name: formData.accountant_name, accountant_firm: formData.accountant_firm,
        accountant_address: formData.accountant_address, accountant_email: formData.accountant_email, accountant_phone: formData.accountant_phone,
        bank_name: formData.bank_name, bank_account_name: formData.bank_account_name,
        bank_account_number: formData.bank_account_number, bank_routing_inbound: formData.bank_routing_inbound,
        bank_routing_outbound: formData.bank_routing_outbound, bank_address: formData.bank_address,
        bank_officer: formData.bank_officer, bank_email: formData.bank_email, bank_phone: formData.bank_phone,
        lender_name: formData.lender_name, lender_address: formData.lender_address,
        lender_account_number: formData.lender_account_number, lender_contact_name: formData.lender_contact_name,
        lender_email: formData.lender_email, lender_phone: formData.lender_phone,
        lender_original_amount: formData.lender_original_amount || null, lender_interest_rate: formData.lender_interest_rate || null,
        lender_maturity_date: formData.lender_maturity_date || null,
        tenant_name: formData.tenant_name, lease_sqft: formData.lease_sqft || null,
        lease_rate_per_sqft: formData.lease_rate_per_sqft || null, lease_start_date: formData.lease_start_date || null,
        lease_expiration_date: formData.lease_expiration_date || null,
        cash_balance: formData.cash_balance, status: formData.status,
        hide_percentages: formData.hide_percentages || false, info_current: formData.info_current || false,
        distributions_current: formData.distributions_current || false,
        issue_financials: formData.issue_financials !== false,
        officer_1_name: formData.officer_1_name, officer_1_role: formData.officer_1_role, officer_1_director: formData.officer_1_director,
        officer_2_name: formData.officer_2_name, officer_2_role: formData.officer_2_role, officer_2_director: formData.officer_2_director,
        officer_3_name: formData.officer_3_name, officer_3_role: formData.officer_3_role, officer_3_director: formData.officer_3_director,
        officer_4_name: formData.officer_4_name, officer_4_role: formData.officer_4_role, officer_4_director: formData.officer_4_director,
        officer_5_name: formData.officer_5_name, officer_5_role: formData.officer_5_role, officer_5_director: formData.officer_5_director,
        officer_6_name: formData.officer_6_name, officer_6_role: formData.officer_6_role, officer_6_director: formData.officer_6_director,
        entity_type: formData.entity_type, date_formed: formData.date_formed, state_formed: formData.state_formed,
        ein_number: formData.ein_number, state_id_number: formData.state_id_number, manager_name: formData.manager_name,
        minute_book_location: formData.minute_book_location, corporate_seal_location: formData.corporate_seal_location
      }, { headers: getAuthHeaders() });
      toast.success('LLC updated successfully');
      setEditDialogOpen(false);
      setEditPropertyDialogOpen(false);
      setEditAccountantDialogOpen(false);
      setEditBankDialogOpen(false);
      setEditLenderDialogOpen(false);
      setEditTenantDialogOpen(false);
      fetchData(llcId);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update LLC');
    } finally {
      setFormLoading(false);
    }
  };

  const handleFieldUpdate = async (updates) => {
    try {
      const updatedData = { ...llc, ...updates };
      await axios.put(`${API}/llcs/${llcId}`, updatedData, { headers: getAuthHeaders() });
      setLLC(updatedData);
      setFormData(updatedData);
    } catch (error) {
      toast.error('Failed to update field');
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/llcs/${llcId}`, { headers: getAuthHeaders() });
      toast.success('LLC deleted successfully');
      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete LLC');
    }
  };

  const handleAddOwnership = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    try {
      await axios.post(`${API}/ownerships`, {
        llc_id: llcId, investor_id: ownershipForm.investor_id, percentage: parseFloat(ownershipForm.percentage)
      }, { headers: getAuthHeaders() });
      toast.success('Ownership added successfully');
      setOwnershipDialogOpen(false);
      setOwnershipForm({ investor_id: '', percentage: '' });
      fetchData(llcId);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add ownership');
    } finally {
      setFormLoading(false);
    }
  };

  const handleDeleteOwnership = async (ownershipId) => {
    try {
      await axios.delete(`${API}/ownerships/${ownershipId}`, { headers: getAuthHeaders() });
      toast.success('Ownership removed');
      fetchData(llcId);
    } catch (error) {
      toast.error('Failed to remove ownership');
    }
  };

  // Cloudinary upload
  const uploadToCloudinary = async (file) => {
    setIsUploading(true);
    setUploadProgress(0);
    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      throw new Error(`File too large. Maximum size is 10MB. Your file is ${(file.size / (1024 * 1024)).toFixed(2)}MB`);
    }
    try {
      const sigRes = await axios.get(`${API}/cloudinary/signature?llc_id=${llcId}`, { headers: getAuthHeaders() });
      const sig = sigRes.data;
      const isPdf = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
      const resourceType = isPdf ? 'raw' : 'auto';
      const uploadFormData = new FormData();
      uploadFormData.append('file', file);
      uploadFormData.append('api_key', sig.api_key);
      uploadFormData.append('timestamp', sig.timestamp.toString());
      uploadFormData.append('signature', sig.signature);
      uploadFormData.append('folder', sig.folder);
      const uploadRes = await axios.post(
        `https://api.cloudinary.com/v1_1/${sig.cloud_name}/${resourceType}/upload`,
        uploadFormData,
        {
          timeout: 120000,
          onUploadProgress: (progressEvent) => {
            const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(percent);
          }
        }
      );
      return { url: uploadRes.data.secure_url, public_id: uploadRes.data.public_id, file_size: uploadRes.data.bytes, file_type: uploadRes.data.format || file.type };
    } catch (error) {
      console.error('Cloudinary upload error:', error);
      if (error.response?.data?.error?.message) throw new Error(`Upload failed: ${error.response.data.error.message}`);
      throw error;
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  // Document handlers
  const handleSaveDocument = async () => {
    setFormLoading(true);
    try {
      let cloudinaryData = {};
      if (selectedFile) {
        const uploadResult = await uploadToCloudinary(selectedFile);
        cloudinaryData = { cloudinary_url: uploadResult.url, cloudinary_public_id: uploadResult.public_id, file_size: uploadResult.file_size, file_type: uploadResult.file_type };
      }
      const payload = { ...documentForm, llc_id: llcId, ...cloudinaryData };
      if (selectedDocument) {
        await axios.put(`${API}/documents/${selectedDocument.id}`, payload, { headers: getAuthHeaders() });
        toast.success('Document updated');
      } else {
        await axios.post(`${API}/documents`, payload, { headers: getAuthHeaders() });
        toast.success('Document added');
      }
      setDocumentDialogOpen(false);
      setSelectedDocument(null);
      setSelectedFile(null);
      setDocumentForm({ document_type: 'other', name: '', description: '' });
      const res = await axios.get(`${API}/documents?llc_id=${llcId}`, { headers: getAuthHeaders() });
      setDocuments(res.data);
    } catch (error) {
      console.error('Document save error:', error);
      toast.error(error.message || 'Failed to save document');
    } finally {
      setFormLoading(false);
    }
  };

  const handleDeleteDocument = async (docId) => {
    try {
      await axios.delete(`${API}/documents/${docId}`, { headers: getAuthHeaders() });
      toast.success('Document deleted');
      setDocuments(documents.filter(d => d.id !== docId));
    } catch (error) {
      toast.error('Failed to delete document');
    }
  };

  const handleViewDocument = async (doc) => {
    if (doc.cloudinary_url) window.open(doc.cloudinary_url, '_blank');
    else toast.error('No download URL available');
  };

  // Communication handlers
  const handleSaveCommunication = async () => {
    setFormLoading(true);
    try {
      const payload = { ...communicationForm, llc_id: llcId };
      if (selectedCommunication) {
        await axios.put(`${API}/communications/${selectedCommunication.id}`, payload, { headers: getAuthHeaders() });
        toast.success('Communication updated');
      } else {
        await axios.post(`${API}/communications`, payload, { headers: getAuthHeaders() });
        toast.success('Communication added');
      }
      setCommunicationDialogOpen(false);
      setSelectedCommunication(null);
      setCommunicationForm({ date: new Date().toISOString().split('T')[0], to_party: '', about: '', notes: '', created_by_name: '' });
      const res = await axios.get(`${API}/communications?llc_id=${llcId}`, { headers: getAuthHeaders() });
      setCommunications(res.data);
    } catch (error) {
      toast.error('Failed to save communication');
    } finally {
      setFormLoading(false);
    }
  };

  const handleDeleteCommunication = async (commId) => {
    try {
      await axios.delete(`${API}/communications/${commId}`, { headers: getAuthHeaders() });
      toast.success('Communication deleted');
      setCommunications(communications.filter(c => c.id !== commId));
    } catch (error) {
      toast.error('Failed to delete communication');
    }
  };

  // Navigation
  const currentIndex = allLLCs.findIndex(l => l.id === llcId);
  const prevLLC = currentIndex > 0 ? allLLCs[currentIndex - 1] : null;
  const nextLLC = currentIndex < allLLCs.length - 1 ? allLLCs[currentIndex + 1] : null;
  const totalOwnership = ownerships.reduce((sum, o) => sum + o.percentage, 0);

  const getStatusBadge = (status) => {
    const variants = { active: 'bg-emerald-100 text-emerald-800', inactive: 'bg-slate-100 text-slate-600', liquidating: 'bg-amber-100 text-amber-800' };
    return <Badge variant="outline" className={variants[status] || variants.active}>{status}</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-amber-700" />
      </div>
    );
  }

  if (!llc) {
    return (
      <div className="text-center py-12">
        <p className="text-slate-500">LLC not found</p>
        <Button onClick={() => navigate('/')} className="mt-4">Back to Dashboard</Button>
      </div>
    );
  }

  const isAdmin = user?.role === 'superadmin' || user?.role === 'admin';
  const canEditLLC = user?.role === 'superadmin' || user?.role === 'manager';
  const canEditTransactions = user?.role === 'superadmin' || user?.role === 'manager';

  const taxYears = [new Date().getFullYear() - 1, new Date().getFullYear()];

  const print1096 = async (year) => {
    try {
      const res = await axios.get(`${API}/llcs/${llcId}/1096/${year}`, { headers: getAuthHeaders() });
      const { llc: l, forms, total_forms, total_amount, master_address, master_phone } = res.data;
      if (forms.length === 0) { toast.info(`No 1099 forms for ${l.name} in ${year}`); return; }
      const fmtC = (a) => new Intl.NumberFormat('en-US', { style: 'decimal', minimumFractionDigits: 2 }).format(a || 0);
      const fmtEIN = (t) => { if (!t) return ''; const d = t.replace(/\D/g, ''); return d.length === 9 ? d.slice(0,2) + '-' + d.slice(2) : t; };
      const fmtPhone = (p) => { if (!p) return ''; const d = p.replace(/\D/g, ''); return d.length === 10 ? '(' + d.slice(0,3) + ') ' + d.slice(3,6) + '-' + d.slice(6) : p; };
      const payerAddr = master_address ? master_address.replace(/\n/g, '<br>') : (l.address || '');
      const payerPhone = fmtPhone(master_phone || l.phone || '');
      const fmtSSN = (t) => { if (!t) return ''; const d = t.replace(/\D/g, ''); return d.length === 9 ? d.slice(0,3) + '-' + d.slice(3,5) + '-' + d.slice(5) : t; };
      const typeLabels = {
        NEC: { name: '1099-NEC', desc: 'Nonemployee Compensation', box1: '1 Nonemployee compensation', box2: '2 Payer made direct sales totaling $5,000 or more', box3: '3', box4: '4 Federal income tax withheld' },
        MISC: { name: '1099-MISC', desc: 'Miscellaneous Information', box1: '1 Rents', box2: '2 Royalties', box3: '3 Other income', box4: '4 Federal income tax withheld' },
        INT: { name: '1099-INT', desc: 'Interest Income', box1: '1 Interest income', box2: '2 Early withdrawal penalty', box3: '3 Interest on U.S. Savings Bonds and Treasury obligations', box4: '4 Federal income tax withheld' },
        DIV: { name: '1099-DIV', desc: 'Dividends and Distributions', box1: '1a Total ordinary dividends', box2: '1b Qualified dividends', box3: '2a Total capital gain distributions', box4: '4 Federal income tax withheld' },
        R: { name: '1099-R', desc: 'Distributions From Pensions, Annuities, Retirement, etc.', box1: '1 Gross distribution', box2: '2a Taxable amount', box3: '3 Capital gain', box4: '4 Federal income tax withheld' },
        S: { name: '1099-S', desc: 'Proceeds From Real Estate Transactions', box1: '2 Gross proceeds', box2: '5 Buyer\'s part of real estate tax', box3: '6', box4: '4 Federal income tax withheld' },
        K: { name: '1099-K', desc: 'Payment Card and Third Party Network Transactions', box1: '1a Gross amount', box2: '1b Card not present transactions', box3: '2 Merchant category code', box4: '4 Federal income tax withheld' },
        B: { name: '1099-B', desc: 'Proceeds From Broker and Barter Exchange Transactions', box1: '1d Proceeds', box2: '1e Cost or other basis', box3: '1f Accrued market discount', box4: '4 Federal income tax withheld' },
        G: { name: '1099-G', desc: 'Certain Government Payments', box1: '1 Unemployment compensation', box2: '2 State or local income tax refunds', box3: '3 Box 2 amount is for tax year', box4: '4 Federal income tax withheld' },
      };
      const copyAForms = forms.map(f => {
        const lb = typeLabels[f.form_type] || typeLabels.NEC;
        return `<div class="form-1099">
          <div class="f-header"><div class="f-year">${year}</div><div class="f-title"><div class="f-name">Form ${lb.name}</div><div class="f-desc">${lb.desc}</div></div><div class="f-copy">Copy A<br><span class="f-small">For Internal Revenue<br>Service Center</span></div></div>
          <div class="f-body">
            <div class="f-row"><div class="f-field f-payer"><div class="f-label">PAYER'S name, street address, city or town, state or province, country, ZIP or foreign postal code, and telephone no.</div><div class="f-val"><strong>${l.name}</strong><br>${payerAddr}<br>${payerPhone}</div></div><div class="f-right"><div class="f-sm"><div class="f-label">PAYER'S TIN</div><div class="f-val f-tin">${fmtEIN(l.ein_number)}</div></div><div class="f-sm"><div class="f-label">RECIPIENT'S TIN</div><div class="f-val f-tin">${fmtSSN(f.vendor_tin)}</div></div></div></div>
            <div class="f-row"><div class="f-field"><div class="f-label">RECIPIENT'S name</div><div class="f-val"><strong>${f.vendor_name}</strong></div></div><div class="f-right"><div class="f-amt"><div class="f-label">${lb.box1}</div><div class="f-amt-val">$ ${fmtC(f.total_paid)}</div></div></div></div>
            <div class="f-row"><div class="f-field"><div class="f-label">Street address (including apt. no.)</div><div class="f-val">${f.vendor_address || ''}</div></div><div class="f-right"><div class="f-amt"><div class="f-label">${lb.box2}</div></div></div></div>
            <div class="f-row"><div class="f-field"><div class="f-label">City or town, state or province, country, and ZIP or foreign postal code</div><div class="f-val">${[f.vendor_city, f.vendor_state, f.vendor_zip].filter(Boolean).join(', ') || ''}</div></div><div class="f-right"><div class="f-amt"><div class="f-label">${lb.box3}</div></div></div></div>
            <div class="f-row"><div class="f-field"><div class="f-label">Account number (see instructions)</div><div class="f-val">&nbsp;</div></div><div class="f-right"><div class="f-amt"><div class="f-label">${lb.box4}</div><div class="f-amt-val">$ 0.00</div></div></div></div>
          </div>
          <div class="f-footer"><div>Form ${lb.name} (Rev. 1-${year})</div><div>Department of the Treasury - Internal Revenue Service</div></div>
        </div>`;
      }).join('');
      const byType = {};
      forms.forEach(f => { const t = f.form_type || 'NEC'; if (!byType[t]) byType[t] = { count: 0, total: 0 }; byType[t].count++; byType[t].total += f.total_paid; });
      const typeRows = Object.entries(byType).map(([t, d]) => `<tr><td>1099-${t}</td><td style="text-align:right">${d.count}</td><td style="text-align:right">$ ${fmtC(d.total)}</td></tr>`).join('');
      const vendorRows = forms.map(f => `<tr><td>${f.vendor_name}</td><td>1099-${f.form_type}</td><td style="text-align:right">${f.transaction_count}</td><td style="text-align:right">$ ${fmtC(f.total_paid)}</td></tr>`).join('');
      const html = `<!DOCTYPE html><html><head><title>1096 Summary - ${l.name} - ${year}</title>
        <style>
          * { margin:0; padding:0; box-sizing:border-box; }
          body { font-family:'Courier New',monospace; font-size:10pt; padding:0.5in; }
          .page { max-width:7.5in; margin:0 auto; page-break-after:always; }
          .header { border:2px solid #000; margin-bottom:20px; }
          .header-top { display:flex; border-bottom:2px solid #000; }
          .header-year { font-size:24pt; font-weight:bold; color:#c00; padding:8px 15px; border-right:1px solid #000; display:flex; align-items:center; }
          .header-title { flex:1; padding:10px; text-align:center; }
          .header-title h1 { font-size:14pt; }
          .header-title p { font-size:9pt; margin-top:2px; }
          .section { padding:10px; border-bottom:1px solid #000; }
          .section:last-child { border-bottom:none; }
          .label { font-size:7pt; color:#333; text-transform:uppercase; margin-bottom:3px; }
          .value { font-size:10pt; }
          .value.large { font-size:14pt; font-weight:bold; }
          .grid2 { display:grid; grid-template-columns:1fr 1fr; gap:10px; }
          table { width:100%; border-collapse:collapse; margin-top:8px; }
          th, td { padding:4px 8px; border:1px solid #999; font-size:9pt; }
          th { background:#eee; text-align:left; font-size:7pt; text-transform:uppercase; }
          .summary-box { background:#f9f9f9; border:2px solid #000; padding:12px; margin-top:15px; }
          .footer { font-size:7pt; color:#666; margin-top:10px; display:flex; justify-content:space-between; }
          @page { margin:0.4in; size:letter; }
          @media print { body { padding:0; } }
        </style></head><body>
        <div class="page">
          <div class="header">
            <div class="header-top">
              <div class="header-year">${year}</div>
              <div class="header-title">
                <h1>Form 1096</h1>
                <p>Annual Summary and Transmittal of U.S. Information Returns</p>
              </div>
            </div>
            <div class="section">
              <div class="label">Filer's name, address, and telephone number</div>
              <div class="value">
                <strong>${l.name}</strong><br>
                ${payerAddr}<br>
                ${payerPhone}
              </div>
            </div>
            <div class="section">
              <div class="grid2">
                <div><div class="label">Employer identification number (EIN)</div><div class="value" style="font-size:12pt;font-weight:bold;letter-spacing:1px">${fmtEIN(l.ein_number)}</div></div>
                <div><div class="label">Tax Year</div><div class="value large">${year}</div></div>
              </div>
            </div>
          </div>
          <div class="summary-box">
            <div class="grid2" style="margin-bottom:10px">
              <div><div class="label">Total number of forms</div><div class="value large">${total_forms}</div></div>
              <div><div class="label">Total amount reported</div><div class="value large">$ ${fmtC(total_amount)}</div></div>
            </div>
            <div class="label">Summary by form type</div>
            <table><thead><tr><th>Form Type</th><th style="text-align:right">Count</th><th style="text-align:right">Total Amount</th></tr></thead><tbody>${typeRows}</tbody></table>
          </div>
          <div style="margin-top:20px">
            <div class="label">Detail of all recipients</div>
            <table><thead><tr><th>Recipient</th><th>Form Type</th><th style="text-align:right">Txns</th><th style="text-align:right">Amount</th></tr></thead><tbody>${vendorRows}</tbody></table>
          </div>
          <div class="footer">
            <div>Form 1096 (Rev. 1-${year})</div>
            <div>Department of the Treasury - Internal Revenue Service</div>
          </div>
        </div>
        <div class="page-break"></div>
        ${copyAForms}
        </body></html>`;
      const styledHtml = html.replace('</style>', `
          .form-1099 { border:2px solid #000; max-width:7in; margin:0 auto 0.25in; page-break-inside:avoid; font-family:'Courier New',monospace; }
          .f-header { display:flex; border-bottom:2px solid #000; }
          .f-year { font-size:24pt; font-weight:bold; color:#c00; padding:8px 15px; border-right:1px solid #000; display:flex; align-items:center; }
          .f-title { flex:1; padding:8px 12px; text-align:center; }
          .f-name { font-size:14pt; font-weight:bold; }
          .f-desc { font-size:9pt; margin-top:2px; }
          .f-copy { padding:8px 12px; border-left:1px solid #000; text-align:center; font-size:10pt; font-weight:bold; }
          .f-small { font-size:8pt; font-weight:normal; }
          .f-body { }
          .f-row { display:flex; border-bottom:1px solid #000; min-height:45px; }
          .f-row:last-child { border-bottom:none; }
          .f-field { flex:1; padding:4px 8px; border-right:1px solid #000; }
          .f-field.f-payer { min-height:70px; }
          .f-right { width:220px; }
          .f-label { font-size:7pt; color:#333; text-transform:uppercase; margin-bottom:2px; }
          .f-val { font-size:10pt; padding:2px 0; }
          .f-val.f-tin { font-size:12pt; font-weight:bold; letter-spacing:1px; }
          .f-sm { padding:4px 8px; border-bottom:1px solid #000; min-height:32px; }
          .f-sm:last-child { border-bottom:none; }
          .f-amt { padding:4px 8px; min-height:45px; }
          .f-amt-val { font-size:14pt; font-weight:bold; text-align:right; padding-top:4px; }
          .f-footer { display:flex; justify-content:space-between; padding:4px 8px; border-top:2px solid #000; font-size:7pt; color:#666; }
        </style>`);
      const w = openPrintWindow(styledHtml);
      if (!w) { toast.error('Popup blocked. Please allow popups to print.'); return; }
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to generate 1096'); }
  };

  return (
    <div className="space-y-5" data-testid="llc-detail">
      {/* Header */}
      <div className="bg-gradient-to-r from-slate-800 via-slate-700 to-slate-800 rounded-xl p-4 shadow-lg">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate('/')} data-testid="back-btn" className="bg-white/10 hover:bg-white/20 text-white border-0" title="Back to Dashboard">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <Button variant="ghost" size="icon" onClick={() => prevLLC && navigate(`/llc/${prevLLC.id}`)} disabled={!prevLLC} title={prevLLC ? `Previous: ${prevLLC.name}` : 'No previous LLC'} data-testid="prev-llc-btn" className={prevLLC ? "bg-amber-500/20 hover:bg-amber-500/30 text-amber-300 border-0" : "opacity-30 text-white/50"}>
              <ChevronLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-3">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center shadow-lg ${llc.llc_type === 'real_estate' ? 'bg-indigo-500' : 'bg-amber-500'}`}>
                {llc.llc_type === 'real_estate' ? <Building2 className="w-6 h-6 text-white" /> : <Briefcase className="w-6 h-6 text-white" />}
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h1 className="text-2xl sm:text-3xl font-serif font-semibold text-white">{llc.name}</h1>
                  {llc.info_current && <Star className="w-5 h-5 fill-amber-400 text-amber-400" title="Information verified and current" />}
                  {llc.distributions_current && <Star className="w-5 h-5 fill-blue-400 text-blue-400" title="All distributions and admin is current" />}
                </div>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-sm text-slate-300 capitalize">{llc.llc_type.replace('_', ' ')}</span>
                  {getStatusBadge(llc.status)}
                  {allLLCs.length > 1 && (
                    <span className="text-xs text-slate-400 bg-white/10 px-2 py-0.5 rounded-full">{currentIndex + 1} of {allLLCs.length}</span>
                  )}
                </div>
              </div>
            </div>
            <Button variant="ghost" size="icon" onClick={() => nextLLC && navigate(`/llc/${nextLLC.id}`)} disabled={!nextLLC} title={nextLLC ? `Next: ${nextLLC.name}` : 'No next LLC'} data-testid="next-llc-btn" className={nextLLC ? "bg-amber-500/20 hover:bg-amber-500/30 text-amber-300 border-0" : "opacity-30 text-white/50"}>
              <ChevronRight className="w-5 h-5" />
            </Button>
          </div>
          <div className="flex flex-col items-end gap-2">
            {isAdmin && (
              <div className="flex gap-2">
                {taxYears.map(yr => (
                  <Button key={yr} variant="outline" onClick={() => print1096(yr)} data-testid={`print-1096-${yr}`} className="bg-white/10 hover:bg-white/20 text-white border-white/20 hover:border-white/40">
                    <Printer className="w-4 h-4 mr-1" />1096-{yr}
                  </Button>
                ))}
                <Button variant="outline" onClick={() => setEditDialogOpen(true)} data-testid="edit-llc-btn" className="bg-white/10 hover:bg-white/20 text-white border-white/20 hover:border-white/40">
                  <Edit className="w-4 h-4 mr-2" />Edit
                </Button>
                <Button variant="outline" className="bg-rose-500/20 hover:bg-rose-500/30 text-rose-300 border-rose-500/30 hover:border-rose-500/50" onClick={() => setDeleteDialogOpen(true)} data-testid="delete-llc-btn">
                  <Trash2 className="w-4 h-4 mr-2" />Delete
                </Button>
              </div>
            )}
            <div className="flex flex-col gap-1 text-xs">
              {llc.last_activity_date && (
                <div className="flex items-center gap-1.5 text-slate-400">
                  <Calendar className="w-3.5 h-3.5" />
                  <span>{new Date(llc.last_activity_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} - Last Activity</span>
                </div>
              )}
              {llc.last_accounting_date && (
                <div className="flex items-center gap-1.5 text-slate-400">
                  <Calendar className="w-3.5 h-3.5" />
                  <span>{new Date(llc.last_accounting_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} - Last Accounting</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        <Card className="border-slate-200 bg-gradient-to-br from-amber-50 to-amber-100/50 border-l-4 border-l-amber-500 shadow-sm">
          <CardContent className="pt-4 pb-4 px-4">
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0 flex-1">
                <p className="text-[11px] text-amber-700 font-semibold uppercase tracking-wider">Checking</p>
                <p className="text-xl font-semibold text-slate-800 mt-1 tabular-nums">{formatCurrency(balanceSheet?.current_assets?.Checking || 0)}</p>
              </div>
              <div className="w-10 h-10 bg-amber-200/60 rounded-lg flex items-center justify-center flex-shrink-0">
                <DollarSign className="w-5 h-5 text-amber-700" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200 bg-gradient-to-br from-indigo-50 to-indigo-100/50 border-l-4 border-l-indigo-500 shadow-sm">
          <CardContent className="pt-4 pb-4 px-4">
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0 flex-1">
                <p className="text-[11px] text-indigo-700 font-semibold uppercase tracking-wider">Total Assets</p>
                <p className="text-xl font-semibold text-slate-800 mt-1 tabular-nums">{formatCurrency(balanceSheet?.total_assets || 0)}</p>
              </div>
              <div className="w-10 h-10 bg-indigo-200/60 rounded-lg flex items-center justify-center flex-shrink-0">
                <Building2 className="w-5 h-5 text-indigo-700" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200 bg-gradient-to-br from-purple-50 to-purple-100/50 border-l-4 border-l-purple-500 shadow-sm">
          <CardContent className="pt-4 pb-4 px-4">
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0 flex-1">
                <p className="text-[11px] text-purple-700 font-semibold uppercase tracking-wider">3rd Party Liab.</p>
                <p className="text-xl font-semibold text-slate-800 mt-1 tabular-nums">{formatCurrency(llc.third_party_liabilities || 0)}</p>
              </div>
              <div className="w-10 h-10 bg-purple-200/60 rounded-lg flex items-center justify-center flex-shrink-0">
                <CreditCard className="w-5 h-5 text-purple-700" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200 bg-gradient-to-br from-emerald-50 to-emerald-100/50 border-l-4 border-l-emerald-500 shadow-sm">
          <CardContent className="pt-4 pb-4 px-4">
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0 flex-1">
                <p className="text-[11px] text-emerald-700 font-semibold uppercase tracking-wider">{new Date().getFullYear()} Income</p>
                <p className="text-xl font-semibold text-slate-800 mt-1 tabular-nums">{formatCurrency(llc.total_income)}</p>
              </div>
              <div className="w-10 h-10 bg-emerald-200/60 rounded-lg flex items-center justify-center flex-shrink-0">
                <TrendingUp className="w-5 h-5 text-emerald-700" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200 bg-gradient-to-br from-rose-50 to-rose-100/50 border-l-4 border-l-rose-500 shadow-sm">
          <CardContent className="pt-4 pb-4 px-4">
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0 flex-1">
                <p className="text-[11px] text-rose-700 font-semibold uppercase tracking-wider">{new Date().getFullYear()} Expenses</p>
                <p className="text-xl font-semibold text-slate-800 mt-1 tabular-nums">{formatCurrency(llc.total_expenses)}</p>
              </div>
              <div className="w-10 h-10 bg-rose-200/60 rounded-lg flex items-center justify-center flex-shrink-0">
                <TrendingDown className="w-5 h-5 text-rose-700" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Information Cards */}
      <InfoPanels
        llc={llc} canEditLLC={canEditLLC}
        onEditProperty={() => setEditPropertyDialogOpen(true)}
        onEditAccountant={() => setEditAccountantDialogOpen(true)}
        onEditBank={() => setEditBankDialogOpen(true)}
        onEditLender={() => setEditLenderDialogOpen(true)}
        onEditTenant={() => setEditTenantDialogOpen(true)}
        onPropertyLookup={() => setPropertyLookupOpen(true)}
      />

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="flex w-full bg-slate-800 p-1 rounded-xl shadow-md gap-1 overflow-x-auto">
          {[
            { value: 'communications', icon: MessageSquare, label: 'Comms' },
            { value: 'documents', icon: FolderOpen, label: 'Docs' },
            { value: 'financials', icon: FileText, label: 'Financials' },
            { value: 'governance', icon: Shield, label: 'Governance' },
            { value: 'open-tasks', icon: ListTodo, label: 'Tasks', badge: llcTasks.length > 0 ? llcTasks.length : null },
            { value: 'ownership', icon: Users, label: 'Ownership' },
            { value: 'transactions', icon: CreditCard, label: 'Txns' },
            { value: 'valuation', icon: TrendingUp, label: 'Valuation' },
          ].map(({ value, icon: Icon, label, badge }) => (
            <TabsTrigger
              key={value} value={value} data-testid={`tab-${value}`}
              className="flex-1 whitespace-nowrap data-[state=active]:bg-amber-500 data-[state=active]:text-white data-[state=active]:shadow-lg text-slate-300 text-xs md:text-sm font-medium rounded-lg transition-all px-2 md:px-3 py-2"
            >
              <Icon className="w-4 h-4 mr-1.5" />
              <span>{label}</span>
              {badge && (
                <span className="ml-1.5 px-1.5 py-0.5 text-xs font-bold bg-amber-600 text-white rounded-full min-w-[18px] text-center">{badge}</span>
              )}
            </TabsTrigger>
          ))}
        </TabsList>

        <OwnershipTab ownerships={ownerships} llc={llc} user={user} canEditLLC={canEditLLC} totalOwnership={totalOwnership} onAddOwnership={() => setOwnershipDialogOpen(true)} onDeleteOwnership={handleDeleteOwnership} />
        <ValuationTab llc={llc} balanceSheet={balanceSheet} ownerships={ownerships} user={user} />
        <TransactionsTab transactions={transactions} canEditTransactions={canEditTransactions} llcId={llcId} />
        <FinancialsTab transactions={transactions} llcId={llcId} />
        <GovernanceTab llc={llc} canEditLLC={canEditLLC} onFieldUpdate={handleFieldUpdate} />
        <DocumentsTab
          documents={documents} canEditLLC={canEditLLC}
          onAddDocument={() => { setSelectedDocument(null); setDocumentForm({ document_type: 'other', name: '', description: '', is_password_protected: false }); setDocumentDialogOpen(true); }}
          onEditDocument={(doc) => { setSelectedDocument(doc); setDocumentForm({ document_type: doc.document_type, name: doc.name, description: doc.description || '', is_password_protected: doc.is_password_protected || false }); setDocumentDialogOpen(true); }}
          onDeleteDocument={handleDeleteDocument} onViewDocument={handleViewDocument}
        />
        <CommunicationsTab
          communications={communications} canEditLLC={canEditLLC}
          onAddCommunication={() => { setSelectedCommunication(null); setCommunicationForm({ date: new Date().toISOString().split('T')[0], to_party: '', about: '', notes: '', created_by_name: user?.name || '' }); setCommunicationDialogOpen(true); }}
          onEditCommunication={(comm) => { setSelectedCommunication(comm); setCommunicationForm({ date: comm.date, to_party: comm.to_party, about: comm.about, notes: comm.notes || '', created_by_name: comm.created_by_name || '' }); setCommunicationDialogOpen(true); }}
          onDeleteCommunication={handleDeleteCommunication}
          onViewCommunication={(comm) => { setViewingCommunication(comm); setCommunicationViewOpen(true); }}
        />
        <TasksTab llcTasks={llcTasks} llcId={llcId} getAuthHeaders={getAuthHeaders} onTaskCreated={() => fetchData(llcId)} />
      </Tabs>

      {/* Dialogs */}
      <DocumentDialog
        open={documentDialogOpen}
        onOpenChange={(open) => { setDocumentDialogOpen(open); if (!open) { setSelectedFile(null); setUploadProgress(0); } }}
        selectedDocument={selectedDocument} documentForm={documentForm} setDocumentForm={setDocumentForm}
        selectedFile={selectedFile} setSelectedFile={setSelectedFile}
        uploadProgress={uploadProgress} isUploading={isUploading} formLoading={formLoading} onSave={handleSaveDocument}
      />
      <CommunicationEditDialog open={communicationDialogOpen} onOpenChange={setCommunicationDialogOpen} selectedCommunication={selectedCommunication} communicationForm={communicationForm} setCommunicationForm={setCommunicationForm} formLoading={formLoading} onSave={handleSaveCommunication} />
      <CommunicationViewDialog
        open={communicationViewOpen} onOpenChange={setCommunicationViewOpen} viewingCommunication={viewingCommunication} canEdit={canEditLLC}
        onEditClick={() => { setCommunicationViewOpen(false); setSelectedCommunication(viewingCommunication); setCommunicationForm({ date: viewingCommunication.date, to_party: viewingCommunication.to_party, about: viewingCommunication.about, notes: viewingCommunication.notes || '', created_by_name: viewingCommunication.created_by_name || '' }); setCommunicationDialogOpen(true); }}
      />
      <EditLLCDialog open={editDialogOpen} onOpenChange={setEditDialogOpen} formData={formData} setFormData={setFormData} onSubmit={handleUpdate} formLoading={formLoading} />
      <AddOwnershipDialog open={ownershipDialogOpen} onOpenChange={setOwnershipDialogOpen} investors={investors} ownershipForm={ownershipForm} setOwnershipForm={setOwnershipForm} totalOwnership={totalOwnership} onSubmit={handleAddOwnership} formLoading={formLoading} />
      <EditInfoDialog open={editPropertyDialogOpen} onOpenChange={setEditPropertyDialogOpen} dialogType="property" formData={formData} setFormData={setFormData} onSubmit={handleUpdate} formLoading={formLoading} />
      <EditInfoDialog open={editAccountantDialogOpen} onOpenChange={setEditAccountantDialogOpen} dialogType="accountant" formData={formData} setFormData={setFormData} onSubmit={handleUpdate} formLoading={formLoading} />
      <EditInfoDialog open={editBankDialogOpen} onOpenChange={setEditBankDialogOpen} dialogType="bank" formData={formData} setFormData={setFormData} onSubmit={handleUpdate} formLoading={formLoading} />
      <EditInfoDialog open={editLenderDialogOpen} onOpenChange={setEditLenderDialogOpen} dialogType="lender" formData={formData} setFormData={setFormData} onSubmit={handleUpdate} formLoading={formLoading} />
      <EditInfoDialog open={editTenantDialogOpen} onOpenChange={setEditTenantDialogOpen} dialogType="tenant" formData={formData} setFormData={setFormData} onSubmit={handleUpdate} formLoading={formLoading} />
      <PropertyLookupDialog open={propertyLookupOpen} onOpenChange={setPropertyLookupOpen} llc={llc} />
      <DeleteLLCDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen} llcName={llc.name} onDelete={handleDelete} dataCount={transactions.length + documents.length + communications.length + ownerships.length} />
    </div>
  );
};

export default LLCDetail;
